import React, { useState } from 'react';
import { FileTextIcon, PrinterIcon, DownloadIcon, EyeIcon, CheckCircleIcon, ClipboardCheckIcon, BuildingIcon, TruckIcon, PackageIcon, CalendarIcon, CircleDollarSignIcon, MapPinIcon } from 'lucide-react';
import { QuotePreview } from './QuotePreview';
export function QuoteReviewStep({
  quoteData,
  quoteType
}) {
  const [showPreview, setShowPreview] = useState(false);
  // Generate a random quote number
  const quoteNumber = `Q${Math.floor(Math.random() * 100000).toString().padStart(5, '0')}`;
  // Calculate totals
  const calculateSubtotal = () => {
    if (quoteType === 'Pivot') {
      return quoteData.pivots.reduce((sum, pivot) => sum + (pivot.price || 0), 0);
    } else if (quoteType === 'Spare Parts') {
      return quoteData.parts.reduce((sum, part) => sum + (part.quantity * part.unitPrice || 0), 0);
    } else {
      return quoteData.items.reduce((sum, item) => sum + (item.price || 0), 0);
    }
  };
  const subtotal = calculateSubtotal();
  const discount = quoteData.quoteDetails?.discount || 0;
  const discountAmount = subtotal * (discount / 100);
  const shippingTotal = quoteData.shipping?.shipments?.reduce((sum, shipment) => sum + (shipment.totalPrice || 0), 0) || 0;
  const total = subtotal - discountAmount + shippingTotal;
  return <div className="space-y-8" data-id="element-4722">
      <div className="text-white text-xl font-medium mb-6" data-id="element-4723">Quote Review</div>
      {/* Summary Card */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-blue-800/30" data-id="element-4724">
        <div className="flex items-center mb-4" data-id="element-4725">
          <ClipboardCheckIcon size={20} className="text-green-400 mr-2" data-id="element-4726" />
          <h3 className="text-white font-medium" data-id="element-4727">Quote Summary</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-4728">
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4729">
            <div className="text-sm text-blue-300 mb-1" data-id="element-4730">Quote Number</div>
            <div className="text-white font-medium" data-id="element-4731">{quoteNumber}</div>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4732">
            <div className="text-sm text-blue-300 mb-1" data-id="element-4733">Quote Type</div>
            <div className="text-white font-medium" data-id="element-4734">{quoteType} Quote</div>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4735">
            <div className="text-sm text-blue-300 mb-1" data-id="element-4736">Issue Date</div>
            <div className="text-white font-medium" data-id="element-4737">
              {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4738">
          {/* Customer Information */}
          <div data-id="element-4739">
            <div className="flex items-center mb-3" data-id="element-4740">
              <BuildingIcon size={16} className="text-blue-400 mr-2" data-id="element-4741" />
              <h4 className="text-blue-300 font-medium text-sm" data-id="element-4742">
                Customer Information
              </h4>
            </div>
            <div className="bg-slate-900/30 rounded-lg p-3 mb-3" data-id="element-4743">
              <div className="text-xs text-blue-300 mb-1" data-id="element-4744">Dealer</div>
              <div className="text-white" data-id="element-4745">
                {quoteData.customerInfo?.dealerName || 'Not specified'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-4746">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4747">
                <div className="text-xs text-blue-300 mb-1" data-id="element-4748">Bill To</div>
                <div className="text-white" data-id="element-4749">
                  {quoteData.customerInfo?.billToCompany || 'Not specified'}
                </div>
                <div className="text-sm text-slate-400 mt-1" data-id="element-4750">
                  {quoteData.customerInfo?.billToCountry || ''}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4751">
                <div className="text-xs text-blue-300 mb-1" data-id="element-4752">Ship To</div>
                <div className="text-white" data-id="element-4753">
                  {quoteData.customerInfo?.shipToCompany || 'Not specified'}
                </div>
                <div className="text-sm text-slate-400 mt-1" data-id="element-4754">
                  {quoteData.customerInfo?.shipToCountry || ''}
                </div>
              </div>
            </div>
          </div>
          {/* Quote Details */}
          <div data-id="element-4755">
            <div className="flex items-center mb-3" data-id="element-4756">
              <FileTextIcon size={16} className="text-blue-400 mr-2" data-id="element-4757" />
              <h4 className="text-blue-300 font-medium text-sm" data-id="element-4758">
                Quote Details
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-4759">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4760">
                <div className="text-xs text-blue-300 mb-1" data-id="element-4761">Valid Until</div>
                <div className="text-white" data-id="element-4762">
                  {quoteData.shipping?.quoteValidUntil || 'Not specified'}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4763">
                <div className="text-xs text-blue-300 mb-1" data-id="element-4764">Delivery Date</div>
                <div className="text-white" data-id="element-4765">
                  {quoteData.shipping?.desiredDeliveryDate || 'Not specified'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-4766">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4767">
                <div className="text-xs text-blue-300 mb-1" data-id="element-4768">Payment Terms</div>
                <div className="text-white" data-id="element-4769">
                  {quoteData.shipping?.paymentTerms || 'Not specified'}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-4770">
                <div className="text-xs text-blue-300 mb-1" data-id="element-4771">Incoterm</div>
                <div className="text-white" data-id="element-4772">
                  {quoteData.shipping?.incoterm || 'Not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Items Summary */}
        <div className="mb-6" data-id="element-4773">
          <div className="flex items-center mb-3" data-id="element-4774">
            <PackageIcon size={16} className="text-blue-400 mr-2" data-id="element-4775" />
            <h4 className="text-blue-300 font-medium text-sm" data-id="element-4776">Items Summary</h4>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-4" data-id="element-4777">
            {quoteType === 'Pivot' && <div className="text-white" data-id="element-4778">
                {quoteData.pivots.length} Pivot Machine
                {quoteData.pivots.length !== 1 ? 's' : ''}
              </div>}
            {quoteType === 'Spare Parts' && <div className="text-white" data-id="element-4779">
                {quoteData.parts.length} Spare Part
                {quoteData.parts.length !== 1 ? 's' : ''}
              </div>}
            {quoteType === 'Project' && <div className="text-white" data-id="element-4780">
                {quoteData.items.length} Project Item
                {quoteData.items.length !== 1 ? 's' : ''}
              </div>}
          </div>
        </div>
        {/* Financial Summary */}
        <div data-id="element-4781">
          <div className="flex items-center mb-3" data-id="element-4782">
            <CircleDollarSignIcon size={16} className="text-blue-400 mr-2" data-id="element-4783" />
            <h4 className="text-blue-300 font-medium text-sm" data-id="element-4784">
              Financial Summary
            </h4>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-4" data-id="element-4785">
            <div className="space-y-2 mb-4" data-id="element-4786">
              <div className="flex justify-between" data-id="element-4787">
                <span className="text-blue-300" data-id="element-4788">Subtotal:</span>
                <span className="text-white" data-id="element-4789">
                  {quoteData.shipping?.currency || '$'} {subtotal.toFixed(2)}
                </span>
              </div>
              {discount > 0 && <div className="flex justify-between" data-id="element-4790">
                  <span className="text-blue-300" data-id="element-4791">Discount ({discount}%):</span>
                  <span className="text-green-400" data-id="element-4792">
                    - {quoteData.shipping?.currency || '$'}{' '}
                    {discountAmount.toFixed(2)}
                  </span>
                </div>}
              {shippingTotal > 0 && <div className="flex justify-between" data-id="element-4793">
                  <span className="text-blue-300" data-id="element-4794">Shipping:</span>
                  <span className="text-white" data-id="element-4795">
                    {quoteData.shipping?.currency || '$'}{' '}
                    {shippingTotal.toFixed(2)}
                  </span>
                </div>}
            </div>
            <div className="flex justify-between pt-3 border-t border-blue-800/30" data-id="element-4796">
              <span className="text-white font-medium" data-id="element-4797">Total:</span>
              <span className="text-xl text-white font-bold" data-id="element-4798">
                {quoteData.shipping?.currency || '$'} {total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 justify-center" data-id="element-4799">
        <button onClick={() => setShowPreview(true)} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-4800">
          <EyeIcon size={18} className="mr-2" data-id="element-4801" />
          Preview Quote
        </button>
        <button onClick={() => console.log('Print quote')} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-4802">
          <PrinterIcon size={18} className="mr-2" data-id="element-4803" />
          Print Quote
        </button>
        <button onClick={() => console.log('Download PDF')} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-4804">
          <DownloadIcon size={18} className="mr-2" data-id="element-4805" />
          Download PDF
        </button>
      </div>
      {/* Preview Modal */}
      {showPreview && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-4806">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-auto" data-id="element-4807">
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center" data-id="element-4808">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-4809">
                Quote Preview
              </h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-500" data-id="element-4810">
                <span className="text-2xl" data-id="element-4811">&times;</span>
              </button>
            </div>
            <div className="p-6" data-id="element-4812">
              <QuotePreview quoteData={quoteData} quoteType={quoteType} quoteNumber={quoteNumber} data-id="element-4813" />
            </div>
            <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex justify-end" data-id="element-4814">
              <button onClick={() => setShowPreview(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg mr-3 hover:bg-gray-300 transition-colors" data-id="element-4815">
                Close
              </button>
              <button onClick={() => console.log('Download PDF')} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-4816">
                <DownloadIcon size={16} className="mr-2" data-id="element-4817" />
                Download PDF
              </button>
            </div>
          </div>
        </div>}
    </div>;
}